import { ProductCard } from "@/components/ui/ProductCard/productCard";
import { calculatePricing } from "@/lib/api/pricing";
import { searchProductsByIds } from "@/lib/api/products";
import { PricingData, Product } from "@/lib/models/product";
import { useCartStore } from "@/lib/store/cartStore";
import { useEffect, useState, useRef } from "react";

interface ProductRecomendedProps {
  productsIds?: string[];
  preloadedProducts?: Product[];
  modal?: boolean;
}

export function ProductRecomended({
  productsIds = [],
  preloadedProducts,
  modal = false,
}: ProductRecomendedProps) {
  const [productList, setProductList] = useState<Product[]>([]);
  const [loading, setLoading] = useState(false);

  const { setExchangeRate } = useCartStore();

  const lastProcessedIdsKey = useRef<string>("");

  useEffect(() => {
    async function loadData() {
      // Caso 1: Vienen productos precargados
      if (preloadedProducts && preloadedProducts.length > 0) {
        const currentIdsKey = `preloaded:${preloadedProducts
          .map((p) => p.id || p.sku)
          .sort()
          .join(",")}`;

        if (currentIdsKey === lastProcessedIdsKey.current) return;
        lastProcessedIdsKey.current = currentIdsKey;

        const needsPricing = preloadedProducts.some(
          (p) => !p.price?.secciones || p.price.secciones.length === 0,
        );

        if (!needsPricing) {
          setProductList(preloadedProducts);
          setLoading(false);
          return;
        }

        setLoading(true);
        try {
          const pricingPayload = {
            items: preloadedProducts.map((p: Product) => ({
              sku: p.sku,
            })),
          };

          const priceRes = await calculatePricing(pricingPayload);
          const pricingData = priceRes?.data?.precios || [];

          const tasa = pricingData.find(
            (p: { tasaCambio?: number | null }) =>
              typeof p.tasaCambio === "number",
          )?.tasaCambio;
          if (tasa) {
            setExchangeRate(tasa);
          }

          const finalProducts = preloadedProducts.map((product: Product) => {
            const priceInfo = pricingData.find((p) => p.sku === product.sku);
            return {
              ...product,
              price: priceInfo || product.price,
            };
          });

          setProductList(finalProducts);
        } catch (error) {
          console.error("Error al calcular precios para recomendados:", error);
          setProductList(preloadedProducts);
        } finally {
          setLoading(false);
        }
        return;
      }

      // Caso 2: Vienen IDs para buscar
      const uniqueIds = [...new Set(productsIds.filter(Boolean))].sort();
      const currentIdsKey = `ids:${uniqueIds.join(",")}`;

      if (uniqueIds.length === 0) {
        setProductList([]);
        return;
      }

      if (currentIdsKey === lastProcessedIdsKey.current) return;
      lastProcessedIdsKey.current = currentIdsKey;
      setLoading(true);

      try {
        const items = await searchProductsByIds(uniqueIds);

        if (items.length === 0) {
          setProductList([]);
          return;
        }

        const pricingPayload = {
          items: items.map((p: Product) => ({
            sku: p.sku,
          })),
        };

        // Si el cálculo de precios falla (p. ej. no hay tasa de cambio) se
        // muestran igual los productos con el precio de lista 1 de la búsqueda.
        let pricingData: PricingData[] = [];
        try {
          const priceRes = await calculatePricing(pricingPayload);
          pricingData = priceRes?.data?.precios || [];
        } catch (pricingError) {
          console.error(
            "Error al calcular precios para recomendados:",
            pricingError,
          );
        }

        const tasa = pricingData.find(
          (p: { tasaCambio?: number | null }) =>
            typeof p.tasaCambio === "number",
        )?.tasaCambio;
        if (tasa) {
          setExchangeRate(tasa);
        }

        const finalProducts = items.map((product: Product) => {
          const priceInfo = pricingData.find((p) => p.sku === product.sku);
          return {
            ...product,
            price: priceInfo || product.price,
          };
        });

        setProductList(finalProducts);
      } catch (error) {
        console.error("Error al cargar productos recomendados:", error);
        lastProcessedIdsKey.current = "";
      } finally {
        setLoading(false);
      }
    }

    loadData();
  }, [productsIds, preloadedProducts, setExchangeRate]);

  if (loading && productList.length === 0) {
    return <div className="p-6 text-center">Cargando recomendaciones...</div>;
  }

  if (productList.length === 0) return null;

  return (
    <>
      {!modal && (
        <p className="font-bold text-2xl mb-4 mt-5">Productos relacionados</p>
      )}
      <div
        className={`${
          !modal
            ? "flex overflow-x-auto overflow-y-hidden bg-gray-100 p-6 gap-4"
            : "grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4"
        } rounded-lg`}
      >
        {productList.map((product: Product) => (
          <div
            key={product.id}
            className={`${
              !modal ? "mb-4 max-w-[40vh] min-w-[40vh]" : "col-span-1"
            }`}
          >
            <ProductCard product={product} shorted={true} recomended={modal} />
          </div>
        ))}
      </div>
    </>
  );
}
